import { Empty, Spin } from 'antd';
import moment from 'moment';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    Bar,
    CartesianGrid,
    ComposedChart,
    Label,
    Legend,
    Line,
    ResponsiveContainer,
    Surface,
    Symbols,
    Tooltip,
    TooltipProps,
    XAxis,
    YAxis,
} from 'recharts';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { RootState } from '../../../../../../store/rootReducer';
import { distributionType } from './Utils';

const formatter = (value: string | number) => `${value}%`;

const Performances: React.FC = () => {
    const loading = useSelector((state: RootState) => state.investments.performancesLoading);
    const performaces = useSelector((state: RootState) => state.investments.performances);

    const fundPerformanceComment = useSelector(
        (state: RootState) => state.investments.investmentDetails?.deal?.fundDetails?.fundPerformance?.comment,
    );
    const data = useMemo(() => {
        let list: {
            name: string;
            benchmarkRate: number;
            benchmarkCRate: number;
            rate: number;
            cRate: number;
        }[] = [];
        performaces?.performances?.forEach((p) => {
            list.push({
                name: `${distributionType('Monthly', p.month || 0)} ${moment(`1/10/${p.year}`).format('YY')}`,
                benchmarkCRate: p.benchmarkCRate,
                benchmarkRate: p.benchmarkRate,
                cRate: p.cRate,
                rate: p.rate,
            });
        });
        return list;
    }, [performaces]);

    return (
        <div className="pb-4 px-2 md:px-6 font-normal text-sm">
            {!loading && (
                <>
                    {data.length > 0 && (
                        <ResponsiveContainer width="100%" height={400}>
                            <ComposedChart
                                data={data}
                                width={600}
                                height={400}
                                margin={{
                                    top: 20,
                                    right: 10,
                                    bottom: 20,
                                    left: 0,
                                }}
                            >
                                <defs>
                                    <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#6AD3F3" stopOpacity={0.9} />
                                        <stop offset="100%" stopColor="#6AD3F3" stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="colorPv" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#37F8B6" stopOpacity={0.9} />
                                        <stop offset="100%" stopColor="#37F8B6" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid stroke="#ededed" />

                                <XAxis dataKey="name" stroke="#ededed" tick={{ fill: '#000000', fontSize: '0.8rem' }} />

                                <YAxis
                                    yAxisId="left"
                                    tickLine={false}
                                    axisLine={false}
                                    stroke="#ededed"
                                    type="number"
                                    allowDataOverflow={false}
                                    domain={['auto', 'auto']}
                                    tickFormatter={formatter}
                                    tick={{ fill: '#000000', fontSize: '0.7rem' }}
                                >
                                    <Label
                                        value={'Monthly Return'}
                                        position="insideLeft"
                                        angle={-90}
                                        style={{ textAnchor: 'middle' }}
                                    />
                                </YAxis>

                                <YAxis
                                    yAxisId="right"
                                    tickFormatter={formatter}
                                    orientation="right"
                                    tickLine={false}
                                    axisLine={false}
                                    type="number"
                                    allowDataOverflow={false}
                                    domain={['auto', 'auto']}
                                    stroke="#ededed"
                                    tick={{ fill: '#000000', fontSize: '0.8rem' }}
                                >
                                    <Label
                                        value="Cumulative Return"
                                        position="insideRight"
                                        angle={-90}
                                        style={{ textAnchor: 'middle' }}
                                    />
                                </YAxis>

                                <Legend
                                    iconType="circle"
                                    content={renderLegend}
                                    name={`${performaces?.benchmarkName || 'Benchmark'}`}
                                />
                                <Bar barSize={14} yAxisId="left" dataKey="rate" fillOpacity={1} fill="url(#colorUv)" />
                                <Bar
                                    barSize={14}
                                    yAxisId="left"
                                    dataKey="benchmarkRate"
                                    fillOpacity={1}
                                    fill="url(#colorPv)"
                                />
                                <Line yAxisId="right" type="monotone" dataKey="cRate" stroke="#008FFB" />
                                <Line yAxisId="right" type="monotone" dataKey="benchmarkCRate" stroke="#00B97A" />

                                <Tooltip
                                    content={<CustomTooltip />}
                                    labelClassName={`${performaces?.benchmarkName || 'Benchmark'}`}
                                />
                            </ComposedChart>
                        </ResponsiveContainer>
                    )}

                    {fundPerformanceComment && data.length > 0 && (
                        <p className="text-center pb-4 text-xs md:text-sm">{fundPerformanceComment}</p>
                    )}
                    {performaces?.lastUpdatedOn && data.length > 0 && (
                        <span className="text-xs">
                            Data is accurate as of {moment(performaces?.lastUpdatedOn).format('MMM DD YYYY')}
                        </span>
                    )}
                    {!loading && data.length === 0 && (
                        <div className="flex justify-center items-center h-40">
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    <p className="text-lg WorkSans font-semibold text-gray-500">No data available</p>
                                }
                            />
                        </div>
                    )}
                </>
            )}
            {loading && (
                <div className="flex justify-center items-center h-40">
                    <Spin />
                </div>
            )}
        </div>
    );
};

const CustomTooltip = ({ active, payload, label, labelClassName }: TooltipProps<ValueType, NameType>) => {
    const tooltipName: any = {
        benchmarkRate: `${labelClassName} Return`,
        benchmarkCRate: `${labelClassName} Cumulative Return`,
        rate: 'Fund Return',
        cRate: 'Fund Cumulative Return',
    };
    const arr = ['rate', 'cRate', 'benchmarkRate', 'benchmarkCRate'];
    if (active) {
        return (
            <div className="shadow rounded bg-white p-4">
                <p className="label font-semibold">{label}</p>
                <div className="desc">
                    {payload &&
                        arr?.map((p, index) => (
                            <div key={`${index}_${p}`}>
                                {payload[0] && payload[0].payload[p as any] && (
                                    <>
                                        {tooltipName[p as any]} : {payload[0] && payload[0].payload[p as any]}%
                                    </>
                                )}
                            </div>
                        ))}
                </div>
            </div>
        );
    }

    return null;
};

const renderLegend = (props: any) => {
    const { payload, name } = props;
    const tooltipName = {
        benchmarkRate: `${name} Return`,
        benchmarkCRate: `${name} Cumulative Return`,
        rate: 'Fund Return',
        cRate: 'Fund Cumulative Return',
    };
    return (
        <div className="flex justify-center items-center flex-wrap" style={{ textAlign: 'center' }}>
            {payload.map((entry: { color: any; value: 'benchmarkRate' | 'benchmarkCRate' | 'rate' | 'cRate' }) => {
                const { color, value } = entry;
                let style = { paddingRight: 10 };
                return (
                    <span className="flex justify-center items-center" style={style} key={`lead_key_${value}`}>
                        <Surface width={20} height={20}>
                            <Symbols cx={10} cy={10} type="circle" size={100} fill={color} />
                        </Surface>
                        <span className="legend-item-name" style={{ paddingLeft: 6 }}>
                            {tooltipName[value]}
                        </span>
                    </span>
                );
            })}
        </div>
    );
};

export default Performances;
